// Outbound partner links. Every affiliate/partner link goes through the backend redirect (/api/go), which checks
// the partner allow-list, adds the tracking ID once a program is approved (admin → Affiliate programs), and logs
// the click for this site. Pages don't need to know whether a program is live yet.
import { currentSiteId } from '@/config/siteBrands';
import { useEffect, useState } from 'react';
import { trackEvent } from '@/lib/analytics';
import siteConfig from '@/config/siteConfig';

const API = process.env.REACT_APP_BACKEND_URL || '';
// On the live domains the click redirect runs under the site's own address (vercel.json sends /api/* to the
// backend), so a reader hovering a partner link sees tech-n-ai-talks.com/api/go…, not a different company's domain.
const GO_BASE = siteConfig.isMultiDomain ? '' : API;

export function goUrl(url, { site, placement = '' } = {}) {
  if (!url || !/^https?:\/\//.test(url)) return url;
  const s = site || currentSiteId();
  const page = typeof window !== 'undefined' ? window.location.pathname : '';
  const qs = new URLSearchParams({ u: url, s, pl: placement, r: page });
  return `${GO_BASE}/api/go?${qs.toString()}`;
}

// Fire a GA event in the site's own property as well (the server log is the source of truth).
export function onOutboundClick(url, { site, placement = '', partner = '' } = {}) {
  trackEvent('affiliate_click', partner || 'partner', placement || url, undefined, site || currentSiteId());
}

// Partner domains for a site (public list from /api/affiliate-programs), cached per page load.
const domainCache = {};
export function usePartnerDomains(site) {
  const s = site || currentSiteId();
  const [domains, setDomains] = useState(domainCache[s] || []);
  useEffect(() => {
    if (domainCache[s]) return;
    let alive = true;
    fetch(`${API}/api/affiliate-programs?site=${encodeURIComponent(s)}`)
      .then((r) => (r.ok ? r.json() : { programs: [] }))
      .then((d) => {
        domainCache[s] = (d.programs || []).flatMap((p) => p.domains || []);
        if (alive) setDomains(domainCache[s]);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [s]);
  return domains;
}

// If href points at a partner domain, return the tracked /api/go URL; otherwise null.
export function partnerHref(href, domains, opts) {
  if (!href || !/^https?:\/\//.test(href) || !domains?.length) return null;
  try {
    const host = new URL(href).hostname.toLowerCase();
    return domains.some((d) => host === d || host.endsWith(`.${d}`)) ? goUrl(href, opts) : null;
  } catch {
    return null;
  }
}
